import type { BiomeType, Tile, TileEcology, TileDeposits } from '../hex/types';
import type { GameConfig } from '../../config/gameConfig';

type BiomeConfig = {
    type: BiomeType;
    weight: number;
    canopyCap?: [number, number];
    fertility?: [number, number];
    wildlifeCap?: [number, number];
    stoneDeposit?: { chance: number; range: [number, number] };
    ironDeposit?: { chance: number; range: [number, number] };
    goldDeposit?: { chance: number; range: [number, number] };
};

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const rand = (a: number, b: number) => lerp(a, b, Math.random());
const randInt = (a: number, b: number) => Math.floor(rand(a, b + 1));
const SQRT3 = Math.sqrt(3);

function hash2D(ix: number, iy: number, seed: number): number {
    let h = Math.imul(ix, 374761393) ^ Math.imul(iy, 668265263) ^ Math.imul(seed, 1442695041);
    h = (h ^ (h >>> 13)) >>> 0;
    h = Math.imul(h, 1274126177);
    h = (h ^ (h >>> 16)) >>> 0;
    return h / 4294967296;
}

function smoothstep(t: number): number {
    return t * t * (3 - 2 * t);
}

function valueNoise2D(x: number, y: number, seed: number): number {
    const ix = Math.floor(x);
    const iy = Math.floor(y);
    const fx = smoothstep(x - ix);
    const fy = smoothstep(y - iy);

    const v00 = hash2D(ix, iy, seed);
    const v10 = hash2D(ix + 1, iy, seed);
    const v01 = hash2D(ix, iy + 1, seed);
    const v11 = hash2D(ix + 1, iy + 1, seed);

    const vx0 = lerp(v00, v10, fx);
    const vx1 = lerp(v01, v11, fx);
    return lerp(vx0, vx1, fy);
}

function fbm2D(
    x: number,
    y: number,
    seed: number,
    octaves = 6,
    lacunarity = 2,
    gain = 0.5,
): number {
    let sum = 0;
    let amp = 1;
    let freq = 1;
    let norm = 0;
    for (let i = 0; i < octaves; i += 1) {
        const n = valueNoise2D(x * freq, y * freq, seed + i * 1013) * 2 - 1;
        sum += n * amp;
        norm += amp;
        amp *= gain;
        freq *= lacunarity;
    }
    return norm > 0 ? sum / norm : 0;
}

function axialToWorld(q: number, r: number): { x: number; y: number } {
    return { x: q + r * 0.5, y: (r * SQRT3) / 2 };
}

const BIOME_CONFIG: BiomeConfig[] = [
    {
        type: 'forest',
        weight: 30,
        canopyCap: [90, 140],
        fertility: [0.8, 1.1],
        wildlifeCap: [20, 45],
        stoneDeposit: { chance: 0.15, range: [150, 350] },
        ironDeposit: { chance: 0.03, range: [80, 180] },
    },
    {
        type: 'plains',
        weight: 25,
        canopyCap: [20, 50],
        fertility: [1.1, 1.5],
        wildlifeCap: [15, 35],
        stoneDeposit: { chance: 0.06, range: [100, 250] },
        ironDeposit: { chance: 0.01, range: [50, 120] },
    },
    {
        type: 'hills',
        weight: 20,
        canopyCap: [40, 80],
        fertility: [0.8, 1.2],
        wildlifeCap: [15, 35],
        stoneDeposit: { chance: 0.3, range: [250, 600] },
        ironDeposit: { chance: 0.1, range: [150, 350] },
        goldDeposit: { chance: 0.01, range: [80, 160] },
    },
    {
        type: 'mountains',
        weight: 15,
        canopyCap: [0, 25],
        fertility: [0.4, 0.8],
        wildlifeCap: [8, 25],
        stoneDeposit: { chance: 0.7, range: [600, 1200] },
        ironDeposit: { chance: 0.25, range: [250, 600] },
        goldDeposit: { chance: 0.04, range: [120, 260] },
    },
    {
        type: 'wetlands',
        weight: 5,
        canopyCap: [10, 40],
        fertility: [0.8, 1.2],
        wildlifeCap: [30, 60],
        stoneDeposit: { chance: 0.04, range: [80, 200] },
    },
];

const BIOME_BY_TYPE = new Map(BIOME_CONFIG.map((entry) => [entry.type, entry]));

const axialNeighbors = [
    { q: 1, r: 0 },
    { q: 1, r: -1 },
    { q: 0, r: -1 },
    { q: -1, r: 0 },
    { q: -1, r: 1 },
    { q: 0, r: 1 },
];

const SIZE_CONFIG = {
    small: { land: 16, ocean: 8, minStone: 10, minIron: 3, minGold: 1 },
    medium: { land: 32, ocean: 12, minStone: 35, minIron: 10, minGold: 3 },
    huge: { land: 64, ocean: 16, minStone: 120, minIron: 35, minGold: 10 },
};

function keyOf(q: number, r: number, s: number) {
    return `${q},${r},${s}`;
}

function parseKey(key: string): { q: number; r: number; s: number } {
    const [q, r, s] = key.split(',').map(Number);
    return { q, r, s };
}

type LandMaskResult = {
    land: Set<string>;
    heightByKey: Map<string, number>;
    seaLevel: number;
    cells: Array<{ q: number; r: number; s: number; key: string; h: number }>;
};

function buildIslandLandMask(args: {
    landQStart: number;
    landRStart: number;
    landWidth: number;
    landHeight: number;
    seed: number;
}): LandMaskResult {
    const { landQStart, landRStart, landWidth, landHeight, seed } = args;
    const centerQ = landQStart + (landWidth - 1) / 2;
    const centerR = landRStart + (landHeight - 1) / 2;
    const center = axialToWorld(centerQ, centerR);
    const corners = [
        axialToWorld(landQStart, landRStart),
        axialToWorld(landQStart + landWidth - 1, landRStart),
        axialToWorld(landQStart, landRStart + landHeight - 1),
        axialToWorld(landQStart + landWidth - 1, landRStart + landHeight - 1),
    ];
    const radius = Math.max(
        ...corners.map((corner) => Math.hypot(corner.x - center.x, corner.y - center.y)),
    );

    const freq = 0.14; // higher = more coastline detail
    const falloffPow = 2.35; // radial sharpness
    const falloffStrength = 1.2; // pushes edges toward sea
    const edgeWarpFreq = 0.22;
    const edgeWarpStrength = 0.35;

    const cells: LandMaskResult['cells'] = [];
    const heightByKey = new Map<string, number>();
    for (let q = landQStart; q < landQStart + landWidth; q += 1) {
        for (let r = landRStart; r < landRStart + landHeight; r += 1) {
            const s = -q - r;
            const world = axialToWorld(q, r);
            const n = fbm2D(world.x * freq, world.y * freq, seed);
            const edgeWarp =
                fbm2D(world.x * edgeWarpFreq, world.y * edgeWarpFreq, seed + 991) * 0.5;
            const d =
                (Math.hypot(world.x - center.x, world.y - center.y) / radius) *
                (1 + edgeWarpStrength * edgeWarp);
            const falloff = Math.pow(Math.min(1, Math.max(0, d)), falloffPow);
            const h = n - falloff * falloffStrength;
            const key = keyOf(q, r, s);
            heightByKey.set(key, h);
            cells.push({ q, r, s, key, h });
        }
    }

    const targetLandCount = Math.round(cells.length * 0.75);
    const sorted = [...cells].sort((a, b) => b.h - a.h);
    const seaIndex = Math.max(0, Math.min(sorted.length - 1, targetLandCount - 1));
    const seaLevel = sorted[seaIndex]?.h ?? 0;
    const land = new Set<string>(
        sorted.filter((cell) => cell.h >= seaLevel).map((cell) => cell.key),
    );

    return { land, heightByKey, seaLevel, cells };
}

function smoothLandMask(
    land: Set<string>,
    cells: Array<{ q: number; r: number; s: number; key: string }>,
    iterations = 2,
): Set<string> {
    let current = new Set(land);
    for (let iter = 0; iter < iterations; iter += 1) {
        const next = new Set(current);
        for (let c = 0; c < cells.length; c += 1) {
            const cell = cells[c];
            let neighbors = 0;
            for (let i = 0; i < axialNeighbors.length; i += 1) {
                const offset = axialNeighbors[i];
                const nq = cell.q + offset.q;
                const nr = cell.r + offset.r;
                const key = keyOf(nq, nr, -nq - nr);
                if (current.has(key)) neighbors += 1;
            }
            if (current.has(cell.key) && neighbors <= 1) {
                next.delete(cell.key);
            } else if (!current.has(cell.key) && neighbors >= 4) {
                next.add(cell.key);
            }
        }
        current = next;
    }
    return current;
}

function keepLargestComponent(land: Set<string>): Set<string> {
    const visited = new Set<string>();
    let largest: Set<string> = new Set();
    const landKeys = Array.from(land);
    for (let i = 0; i < landKeys.length; i += 1) {
        const key = landKeys[i];
        if (visited.has(key)) continue;
        const component = new Set<string>();
        const queue = [key];
        visited.add(key);
        while (queue.length > 0) {
            const current = queue.pop();
            if (!current) continue;
            component.add(current);
            const { q, r } = parseKey(current);
            for (let j = 0; j < axialNeighbors.length; j += 1) {
                const offset = axialNeighbors[j];
                const nq = q + offset.q;
                const nr = r + offset.r;
                const ns = -nq - nr;
                const nkey = keyOf(nq, nr, ns);
                if (!land.has(nkey) || visited.has(nkey)) continue;
                visited.add(nkey);
                queue.push(nkey);
            }
        }
        if (component.size > largest.size) largest = component;
    }
    return largest;
}

function shuffle<T>(list: T[]): T[] {
    for (let i = list.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
}

function pickBiome(
    weights: Record<BiomeType, number>,
    remaining: Record<BiomeType, number>,
): BiomeType {
    const candidates = Object.entries(weights).filter(([type]) => remaining[type as BiomeType] > 0);
    const total = candidates.reduce((sum, [, weight]) => sum + weight, 0);
    if (total <= 0) {
        return (Object.keys(remaining).find((key) => remaining[key as BiomeType] > 0) ||
            'plains') as BiomeType;
    }
    let roll = Math.random() * total;
    for (const [type, weight] of candidates) {
        roll -= weight;
        if (roll <= 0) return type as BiomeType;
    }
    return candidates[0][0] as BiomeType;
}

function fillAges(
    total: number,
    maxAge: number,
    buckets: number,
): { age: number; amount: number }[] {
    if (total <= 0) return [];
    const entries: { age: number; amount: number }[] = [];
    const picks = Array.from({ length: buckets }, () => randInt(0, maxAge));
    let remaining = total;
    for (let i = 0; i < picks.length; i += 1) {
        const share =
            i === picks.length - 1
                ? remaining
                : randInt(1, Math.max(1, remaining - (picks.length - i)));
        remaining -= share;
        entries.push({ age: picks[i], amount: share });
    }
    return entries;
}

function applyBiomeAttributes(tile: Tile, biome: BiomeConfig): void {
    const ecology: TileEcology = {};
    if (biome.canopyCap)
        ecology.canopyCap = Math.round(rand(biome.canopyCap[0], biome.canopyCap[1]));
    if (biome.fertility)
        ecology.fertility = Number(rand(biome.fertility[0], biome.fertility[1]).toFixed(2));
    if (biome.wildlifeCap)
        ecology.wildlifeCap = Math.round(rand(biome.wildlifeCap[0], biome.wildlifeCap[1]));
    ecology.corruption = 0;
    tile.ecology = ecology;

    const deposits: TileDeposits = {};
    if (biome.stoneDeposit && Math.random() < biome.stoneDeposit.chance) {
        deposits.stoneDeposit = Math.round(
            rand(biome.stoneDeposit.range[0], biome.stoneDeposit.range[1]),
        );
    }
    if (biome.ironDeposit && Math.random() < biome.ironDeposit.chance) {
        deposits.ironDeposit = Math.round(
            rand(biome.ironDeposit.range[0], biome.ironDeposit.range[1]),
        );
    }
    if (biome.goldDeposit && Math.random() < biome.goldDeposit.chance) {
        deposits.goldDeposit = Math.round(
            rand(biome.goldDeposit.range[0], biome.goldDeposit.range[1]),
        );
    }
    if (Object.keys(deposits).length > 0) tile.deposits = deposits;

    if (ecology.canopyCap) {
        const initialTrees = Math.round(ecology.canopyCap * rand(0.6, 0.9));
        tile.resources.trees = fillAges(initialTrees, 24, 3);
    }
    if (ecology.wildlifeCap) {
        const initialWildlife = Math.round(ecology.wildlifeCap * rand(0.4, 0.8));
        tile.resources.wild_life = fillAges(initialWildlife, 8, 3);
    }
}

function applyFish(tile: Tile): void {
    const fishCap = Math.round(rand(180, 360));
    tile.ecology = { ...(tile.ecology || {}), fishCap, corruption: tile.ecology?.corruption ?? 0 };
    const stock = Math.round(fishCap * rand(0.6, 1.0));
    tile.resources.fish = { amount: stock };
}

function ensureDepositMinimum(
    tiles: Tile[],
    count: number,
    type: 'stoneDeposit' | 'ironDeposit' | 'goldDeposit',
    eligible: (tile: Tile) => boolean,
): void {
    let current = tiles.filter((tile) => tile.deposits && tile.deposits[type]).length;
    const candidates = shuffle(
        tiles.filter((tile) => {
            if (!eligible(tile)) return false;
            if (tile.deposits && tile.deposits[type]) return false;
            const config = BIOME_BY_TYPE.get(tile.biome.type);
            if (!config) return false;
            if (type === 'stoneDeposit') return !!config.stoneDeposit;
            if (type === 'ironDeposit') return !!config.ironDeposit;
            return !!config.goldDeposit;
        }),
    );
    let idx = 0;
    while (current < count && idx < candidates.length) {
        const tile = candidates[idx];
        const config = BIOME_BY_TYPE.get(tile.biome.type);
        const depositConfig =
            type === 'stoneDeposit'
                ? config?.stoneDeposit
                : type === 'ironDeposit'
                  ? config?.ironDeposit
                  : config?.goldDeposit;
        if (!depositConfig) {
            idx += 1;
            continue;
        }
        tile.deposits = tile.deposits || {};
        if (!tile.deposits[type]) {
            tile.deposits[type] = Math.round(rand(depositConfig.range[0], depositConfig.range[1]));
            current += 1;
        }
        idx += 1;
    }
}

function spawnHorses(tiles: Tile[], size: 'small' | 'medium' | 'huge'): void {
    const counts = { small: [1, 1], medium: [1, 2], huge: [2, 3] }[size];
    const target = randInt(counts[0], counts[1]);
    const candidates = shuffle(
        tiles.filter((tile) => tile.biome.type === 'plains' || tile.biome.type === 'hills'),
    );
    for (let i = 0; i < target && i < candidates.length; i += 1) {
        const herd = randInt(2, 6);
        candidates[i].hidden = { ...(candidates[i].hidden || {}), horses: herd };
    }
}

export function generateIslandTiles(config: GameConfig): Tile[] {
    const size = SIZE_CONFIG[config.map.size];
    const landWidth = size.land;
    const landHeight = size.land;
    const oceanBuffer = size.ocean;
    const mapWidth = landWidth + oceanBuffer * 2;
    const mapHeight = landHeight + oceanBuffer * 2;
    const tiles: Tile[] = [];

    const qStart = -Math.floor(mapWidth / 2);
    const rStart = -Math.floor(mapHeight / 2);
    const landQStart = -Math.floor(landWidth / 2);
    const landRStart = -Math.floor(landHeight / 2);
    const seed = Number.isFinite(config.map.seed)
        ? config.map.seed!
        : Math.floor(Math.random() * 300000);
    const landMask = buildIslandLandMask({ landQStart, landRStart, landWidth, landHeight, seed });
    let landSet = smoothLandMask(landMask.land, landMask.cells, 2);
    landSet = keepLargestComponent(landSet);

    const landTiles: Tile[] = [];

    for (let qi = 0; qi < mapWidth; qi += 1) {
        for (let ri = 0; ri < mapHeight; ri += 1) {
            const q = qStart + qi;
            const r = rStart + ri;
            const s = -q - r;
            const inLand =
                q >= landQStart &&
                q < landQStart + landWidth &&
                r >= landRStart &&
                r < landRStart + landHeight;

            let biomeType: BiomeType = 'water';
            if (inLand) {
                biomeType = landSet.has(keyOf(q, r, s)) ? 'plains' : 'water';
            }

            const tile: Tile = {
                id: `hex_${q}_${r}_${s}`,
                position: { q, r, s },
                biome: { type: biomeType },
                resources: {},
            };

            tiles.push(tile);
            if (inLand && biomeType !== 'water') {
                landTiles.push(tile);
            }
        }
    }

    const biomeTargets = BIOME_CONFIG.reduce<Record<BiomeType, number>>(
        (acc, entry) => {
            acc[entry.type] = Math.round((landTiles.length * entry.weight) / 100);
            return acc;
        },
        {} as Record<BiomeType, number>,
    );

    const assigned = new Map<string, BiomeType>();
    const remaining = { ...biomeTargets } as Record<BiomeType, number>;
    const order = shuffle([...landTiles]);

    order.forEach((tile) => {
        const neighborCounts: Record<BiomeType, number> = {
            forest: 0,
            plains: 0,
            hills: 0,
            mountains: 0,
            wetlands: 0,
            coast: 0,
            water: 0,
        };
        axialNeighbors.forEach((n) => {
            const nq = tile.position.q + n.q;
            const nr = tile.position.r + n.r;
            const ns = -nq - nr;
            const key = keyOf(nq, nr, ns);
            const neighborBiome = assigned.get(key);
            if (neighborBiome) neighborCounts[neighborBiome] += 1;
        });

        const weights: Record<BiomeType, number> = {} as Record<BiomeType, number>;
        BIOME_CONFIG.forEach((entry) => {
            const neighborBias = neighborCounts[entry.type] * 0.6;
            weights[entry.type] = entry.weight * (1 + neighborBias);
        });

        const choice = pickBiome(weights, remaining);
        assigned.set(keyOf(tile.position.q, tile.position.r, tile.position.s), choice);
        remaining[choice] = Math.max(0, remaining[choice] - 1);
    });

    const tilesByKey = new Map<string, Tile>();
    tiles.forEach((tile) => {
        tilesByKey.set(keyOf(tile.position.q, tile.position.r, tile.position.s), tile);
    });

    landTiles.forEach((tile) => {
        const isCoast = axialNeighbors.some((n) => {
            const nq = tile.position.q + n.q;
            const nr = tile.position.r + n.r;
            const ns = -nq - nr;
            const neighbor = tilesByKey.get(keyOf(nq, nr, ns));
            return !neighbor || neighbor.biome.type === 'water';
        });
        if (isCoast) {
            tile.biome.type = 'coast';
            applyFish(tile);
            tile.ecology = { ...(tile.ecology || {}), corruption: 0 };
            return;
        }
        const biome =
            assigned.get(keyOf(tile.position.q, tile.position.r, tile.position.s)) || 'plains';
        tile.biome.type = biome;
        const configEntry = BIOME_CONFIG.find((entry) => entry.type === biome);
        if (configEntry) applyBiomeAttributes(tile, configEntry);
    });

    tiles.forEach((tile) => {
        if (tile.biome.type === 'water') {
            tile.ecology = { ...(tile.ecology || {}), corruption: tile.ecology?.corruption ?? 0 };
        }
    });

    markWaterTypes(tiles, tilesByKey, qStart, rStart, mapWidth, mapHeight);

    ensureDepositMinimum(
        landTiles,
        size.minStone,
        'stoneDeposit',
        (tile) => tile.biome.type !== 'water',
    );
    ensureDepositMinimum(
        landTiles,
        size.minIron,
        'ironDeposit',
        (tile) => tile.biome.type !== 'water',
    );
    ensureDepositMinimum(
        landTiles,
        size.minGold,
        'goldDeposit',
        (tile) => tile.biome.type === 'hills' || tile.biome.type === 'mountains',
    );

    spawnHorses(landTiles, config.map.size);

    return tiles;
}

function markWaterTypes(
    tiles: Tile[],
    tilesByKey: Map<string, Tile>,
    qStart: number,
    rStart: number,
    mapWidth: number,
    mapHeight: number,
): void {
    const qEnd = qStart + mapWidth - 1;
    const rEnd = rStart + mapHeight - 1;
    const waterTiles = tiles.filter((tile) => tile.biome.type === 'water');
    if (waterTiles.length === 0) return;

    const ocean = new Set<string>();
    const queue: Tile[] = [];
    waterTiles.forEach((tile) => {
        const { q, r, s } = tile.position;
        if (q === qStart || q === qEnd || r === rStart || r === rEnd) {
            const key = keyOf(q, r, s);
            ocean.add(key);
            queue.push(tile);
        }
    });

    while (queue.length > 0) {
        const tile = queue.shift();
        if (!tile) break;
        axialNeighbors.forEach((n) => {
            const nq = tile.position.q + n.q;
            const nr = tile.position.r + n.r;
            const ns = -nq - nr;
            const key = keyOf(nq, nr, ns);
            if (ocean.has(key)) return;
            const neighbor = tilesByKey.get(key);
            if (!neighbor || neighbor.biome.type !== 'water') return;
            ocean.add(key);
            queue.push(neighbor);
        });
    }

    waterTiles.forEach((tile) => {
        const key = keyOf(tile.position.q, tile.position.r, tile.position.s);
        if (ocean.has(key)) {
            tile.biome.waterType = 'ocean';
            applyWaterFish(tile, 1);
            return;
        }
        tile.biome.waterType = 'lake';
        applyWaterFish(tile, 0.5);
    });
}

function applyWaterFish(tile: Tile, multiplier: number): void {
    applyFish(tile);
    if (tile.ecology?.fishCap) {
        tile.ecology.fishCap = Math.max(1, Math.round(tile.ecology.fishCap * multiplier));
    }
    const fish = tile.resources.fish as { amount?: number } | undefined;
    if (fish && typeof fish.amount === 'number') {
        fish.amount = Math.max(0, Math.round(fish.amount * multiplier));
    }
}
